import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { Company, Companies } from './company';
import { ConfigService } from '@docma-mm-core/config/config.service';
import {Project} from '@docma-mm-core/project/project';

const savedProjectCompaniesKey: string = 'mm-savedProjectCompanies';

@Injectable()
export class CompanyService extends BasedataService {

  private projectUrl: string = '/projects/';
  private companyUrl: string = '/companies';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getCompanyUrl() {
    return super.getFullUrl(this.companyUrl);
  }

  private getProjectCompanyUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.companyUrl);
  }


  // get list of all companies
  getNotAssignedCompanies(project: Project, top: number = 10000, skip: number = 0): Observable<Companies> {
    let endpoint = this.getCompanyUrl() + '/notassigned';
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    if (project?.Id) httpParams = httpParams.set('projectId', project.Id);
    // if (onlyGlobalCompanies) httpParams = httpParams.set('$onlyGlobalCompanies', onlyGlobalCompanies.toString())
    httpParams = httpParams.set('$details', '10');
    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // search componies
  searchCompanies(top: number = 10,
                  skip: number = 0,
                  id: number = null,
                  uid: string = null,
                  name1: string = null,
                  name2: string = null,
                  shortName: string = null,
                  city: string = null,
                  cityPOBox: string = null,
                  country: string = null,
                  email: string = null,
                  fax: string = null,
                  phone: string = null,
                  poBox: string = null,
                  street: string = null,
                  web: string = null,
                  zipCode: string = null,
                  zipCodePOBox: string = null,
                  isActive: boolean = null,
                  isInsolvent: boolean = null,
                  usePOBoxAsAddress: boolean = null,
                  viewpoint: number = null,
                  pid: string = null,
                  puid: string = null): Observable<Companies> {
    let endpoint = this.getCompanyUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    if (id) httpParams = httpParams.set('id', id.toString());
    if (uid) httpParams = httpParams.set('uid', uid);
    if (name1) httpParams = httpParams.set('name1', name1);
    if (name2) httpParams = httpParams.set('name2', name2);
    if (shortName) httpParams = httpParams.set('shortName', shortName);
    if (city) httpParams = httpParams.set('city', city);
    if (cityPOBox) httpParams = httpParams.set('cityPOBox', cityPOBox);
    if (country) httpParams = httpParams.set('country', country);
    if (email) httpParams = httpParams.set('email', email);
    if (fax) httpParams = httpParams.set('fax', fax);
    if (phone) httpParams = httpParams.set('phone', phone);
    if (poBox) httpParams = httpParams.set('poBox', poBox);
    if (street) httpParams = httpParams.set('street', street);
    if (web) httpParams = httpParams.set('web', web);
    if (zipCode) httpParams = httpParams.set('zipCode', zipCode);
    if (zipCodePOBox) httpParams = httpParams.set('zipCodePOBox', zipCodePOBox);
    if (isActive) httpParams = httpParams.set('isActive', isActive ? 'true' : 'false');
    if (isInsolvent) httpParams = httpParams.set('isInsolvent', isInsolvent ? 'true' : 'false');
    if (usePOBoxAsAddress) httpParams = httpParams.set('usePOBoxAsAddress', usePOBoxAsAddress ? 'true' : 'false');
    if (viewpoint) httpParams = httpParams.set('viewpoint', viewpoint.toString());
    if (pid) httpParams = httpParams.set('pid', pid);
    if (puid) httpParams = httpParams.set('puid', puid);
    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by id
  getCompanyById(companyId: number): Observable<Company> {
    let endpoint = this.getCompanyUrl() + '/' + companyId;
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by uid
  getCompanyByUid(companyUid: string): Observable<Company> {
    let endpoint = this.getCompanyUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', companyUid);
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new company
  createCompany(company: Company): Observable<Company> {
    let endpoint = this.getCompanyUrl();
    return this.http
      .post<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing company
  updateCompany(company: Company): Observable<Company> {
    let endpoint = this.getCompanyUrl();
    return this.http
      .put<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get companies by project
  getProjectCompanies(projectId: number, top: number = 10, skip: number = 0): Observable<Companies> {
    let endpoint = this.getProjectCompanyUrl(projectId);
    let httpParams = new HttpParams();
    if (top) httpParams = httpParams.set('$top', top.toString());
    if (skip) httpParams = httpParams.set('$skip', skip.toString());
    httpParams = httpParams.set('$details', '10');

    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by id
  getProjectCompanyById(projectId: number, companyId: number): Observable<Company> {
    let endpoint = this.getProjectCompanyUrl(projectId) + '/' + companyId;
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by uid
  getProjectCompanyByUid(projectId: number, companyUid: string): Observable<Company> {
    let endpoint = this.getProjectCompanyUrl(projectId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', companyUid);
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new company
  createProjectCompany(projectId: number, company: Company): Observable<Company> {
    let endpoint = this.getProjectCompanyUrl(projectId);
    return this.http
      .post<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing company
  assignProjectCompany(projectId: number, company: Company): Observable<Company> {
    let endpoint = this.getProjectCompanyUrl(projectId) + '/' + company.Id;

    let httpParams = new HttpParams({fromString: 'includeContacts=true&includeOrders=true'});

  return this.http
      .put<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

    // update existing companies
  assignProjectCompanies(projectId: number, companies: Company[]): Observable<Company[]> {
    const companyIds: { Id: number }[] = companies.map(company => { return { Id: company.Id } })

    let endpoint = this.getProjectCompanyUrl(projectId) + '/';

    let httpParams = new HttpParams({fromString: 'includeContacts=true&includeOrders=true'});

    return this.http
        .put<Company[]>(endpoint, JSON.stringify(companyIds), { headers: this.getHeaders(), params: httpParams })
        .pipe(catchError(e  => this.handleError(e)));
    }

}
